import { useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { DateRangePicker, type DateRange } from "@/components/cinch/DatePicker";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar as DsFilterBar,
  FilterChip,
  FilterClearAll,
} from "@/components/cinch/FilterBar";
import { Button } from "@/components/ui/button";

import { useDashboard, type OpenDeal } from "../api";
import { DealCommitDrawer } from "../components/DealCommitDrawer";
import { CroForecastFilterBar } from "../components/FilterBar";
import { OpenDealsTable, type OpenDealsFilter } from "../components/OpenDealsTable";
import { CroForecastSubNav } from "../components/SubNav";
import { PIPELINES } from "../constants";
import { resolveSearch, type CroForecastSearch } from "../search";

export function CroForecastOpenDealsPage() {
  const search = useSearch({ from: "/cro-forecast/deals" }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);
  const [pipeline, setPipeline] = useState<string>(PIPELINES.SALES);
  const [selected, setSelected] = useState<OpenDeal | null>(null);

  // Filter state — kept local since the date filter already lives in the URL
  // and folding owner/stage/close into the URL would clutter the QR.
  const [ownerNames, setOwnerNames] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [closeRange, setCloseRange] = useState<DateRange>({ start: null, end: null });

  const { data, isLoading, error } = useDashboard({ pipeline, year, week });

  const month = new Date().getMonth() + 1;
  const fyear = new Date().getFullYear();

  // Distinct values for the filter dropdowns, derived from the loaded deals.
  const { ownerOptions, stageOptions } = useMemo(() => {
    const owners = new Set<string>();
    const stages = new Set<string>();
    for (const d of data?.open_deals ?? []) {
      if (d.owner_display_name) owners.add(d.owner_display_name);
      if (d.stage_name) stages.add(d.stage_name);
    }
    return {
      ownerOptions: [...owners].sort(),
      stageOptions: [...stages].sort(),
    };
  }, [data]);

  const fmtDate = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : null);

  const filter: OpenDealsFilter = {
    ownerNames,
    stages,
    closeFrom: fmtDate(closeRange.start),
    closeTo: fmtDate(closeRange.end),
  };

  const hasAnyFilter =
    ownerNames.length > 0 ||
    stages.length > 0 ||
    closeRange.start != null ||
    closeRange.end != null;

  const clearAll = () => {
    setOwnerNames([]);
    setStages([]);
    setCloseRange({ start: null, end: null });
  };

  const rangeLabel =
    closeRange.start || closeRange.end
      ? `${fmtDate(closeRange.start) ?? "…"} → ${fmtDate(closeRange.end) ?? "…"}`
      : null;

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-4 p-4 md:p-6">
        {isLoading && <p className="text-sm text-muted-foreground">Loading open deals…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Failed to load: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={pipeline === PIPELINES.SALES ? "default" : "outline"}
                size="sm"
                onClick={() => setPipeline(PIPELINES.SALES)}
              >
                Sales Pipeline ({data.banner.total_deal_count})
              </Button>
              <Button
                variant={pipeline === PIPELINES.PARTNERSHIPS ? "default" : "outline"}
                size="sm"
                onClick={() => setPipeline(PIPELINES.PARTNERSHIPS)}
              >
                Partnerships
              </Button>
            </div>

            {/* DS FilterBar — Owner / Stage as multi-select FilterChip
                popovers (CheckboxList), close-date as a DateRangePicker.
                Active selections render as ActiveFilterPill chips below
                with a Clear all link. Matches the Pipeline Movement
                filter UX. */}
            <DsFilterBar>
              <FilterChip
                label="Owner"
                count={ownerNames.length}
                valueLabel={
                  ownerNames.length === 1
                    ? ownerNames[0]
                    : ownerNames.length > 1
                    ? `${ownerNames.length} owners`
                    : undefined
                }
              >
                <CheckboxList
                  options={ownerOptions.map((o) => ({ value: o, label: o }))}
                  selected={ownerNames}
                  onChange={setOwnerNames}
                  searchable
                />
              </FilterChip>

              <FilterChip
                label="Stage"
                count={stages.length}
                valueLabel={
                  stages.length === 1
                    ? stages[0]
                    : stages.length > 1
                    ? `${stages.length} stages`
                    : undefined
                }
              >
                <CheckboxList
                  options={stageOptions.map((s) => ({ value: s, label: s }))}
                  selected={stages}
                  onChange={setStages}
                />
              </FilterChip>

              <DateRangePicker
                value={closeRange}
                onChange={setCloseRange}
                aria-label="Close-date range"
              />

              {ownerNames.map((o) => (
                <ActiveFilterPill
                  key={`ow-${o}`}
                  label={o}
                  onRemove={() => setOwnerNames(ownerNames.filter((x) => x !== o))}
                />
              ))}
              {stages.map((s) => (
                <ActiveFilterPill
                  key={`st-${s}`}
                  label={s}
                  onRemove={() => setStages(stages.filter((x) => x !== s))}
                />
              ))}
              {rangeLabel && (
                <ActiveFilterPill
                  label={`Close: ${rangeLabel}`}
                  onRemove={() => setCloseRange({ start: null, end: null })}
                />
              )}

              {hasAnyFilter && <FilterClearAll onClick={clearAll} />}
            </DsFilterBar>

            <OpenDealsTable
              deals={data.open_deals}
              filter={filter}
              onSelect={setSelected}
            />
          </>
        )}
      </div>

      <DealCommitDrawer
        deal={selected}
        forecastYear={fyear}
        forecastMonth={month}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </div>
  );
}

